import { create } from 'zustand';
import type { MeProfileResponseLocal, RoleAssignmentInfoLocal } from '../api/types/MeLocal';
import type { Permission, Scope } from '../api/types/permissions';
import { meApi } from '../api/endpoints/me';
import { SessionExpiredError } from '../api/errors';

export interface AuthUser {
    cognitoSub: string;
    email: string;
    displayName: string;
    phone?: string;
    tenantId: string;
    mfaSetupRequired: boolean;
}

interface AuthState {
    user: AuthUser | null;
    permissions: Set<Permission>;
    roleAssignments: RoleAssignmentInfoLocal[];
    /** DEC-RBAC-027: niveles BYOID del propio tenant (de `/me`). `[]` = BYOID apagado. */
    externalScopeLevels: Scope[];
    loading: boolean;
    lastLoadedAt: number | null;
    ensureLoaded: () => Promise<AuthState>;
    refresh: () => Promise<void>;
    clear: () => void;
    setFromProfile: (profile: MeProfileResponseLocal) => void;
}

let inflightLoad: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    permissions: new Set(),
    roleAssignments: [],
    externalScopeLevels: [],
    loading: false,
    lastLoadedAt: null,

    // DEC-003: GET /me/profile inline como single source of truth (sin polling). Ver DECISIONES.md.
    ensureLoaded: async () => {
        const state = get();
        if (state.user && state.lastLoadedAt && Date.now() - state.lastLoadedAt < 30_000) {
            return state;
        }
        inflightLoad ??= get()
            .refresh()
            .finally(() => {
                inflightLoad = null;
            });
        await inflightLoad;
        return get();
    },

    refresh: async () => {
        set({ loading: true });
        try {
            const profile = await meApi.getProfile();
            get().setFromProfile(profile);
        } catch (err) {
            if (err instanceof SessionExpiredError) get().clear();
        } finally {
            set({ loading: false });
        }
    },

    clear: () => {
        set({
            user: null,
            permissions: new Set(),
            roleAssignments: [],
            externalScopeLevels: [],
            lastLoadedAt: null,
        });
    },

    setFromProfile: (profile) => {
        set({
            user: {
                cognitoSub: profile.cognitoSub,
                email: profile.email,
                displayName: profile.displayName,
                phone: profile.phone,
                tenantId: profile.tenantId,
                mfaSetupRequired: profile.mfaSetupRequired,
            },
            permissions: new Set(profile.permissions),
            roleAssignments: profile.roleAssignments,
            externalScopeLevels: profile.externalScopeLevels,
            lastLoadedAt: Date.now(),
        });
    },
}));

/** Internal test hook — fuerza el reset del inflight lock entre tests. */
export const _resetInflightForTests = (): void => {
    inflightLoad = null;
};
